import React, { Suspense } from 'react';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import '../../../styles/newsletter-signup.css';
import './prose.css';
import { preprocessMarkdown, remarkCallouts } from '../utils/markdownCallouts';

const ReactMarkdown = React.lazy(() => import('react-markdown'));
// Mermaid support is optional. To enable, install:
//   npm i remark-mermaidjs mermaid
// and then import and add the plugin below.
// import remarkMermaid from 'remark-mermaidjs';

// react-markdown v9+ no longer passes an `inline` prop to the `code`
// component override (that was removed as a breaking change), so branching
// on `inline` here always took the "block" path — every inline code span
// rendered as a block-level <div><pre>, which is invalid inside the
// enclosing <p> and broke the sentence around it. Fixed the standard way:
// `code` always renders a plain inline <code>; `pre` (which fenced code
// blocks are wrapped in, but inline code never is) owns the copy-button
// chrome instead.
function CodeBlock({ className, children, ...props }) {
  return <code {...props} className={className}>{children}</code>;
}

function nodeText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (node.props?.children != null) return nodeText(node.props.children);
  return '';
}

function PreBlock({ children, ...props }) {
  const txt = nodeText(children);
  const onCopy = () => navigator.clipboard?.writeText(txt.trim()).catch(() => {});
  return (
    <div className="code-wrap">
      <pre {...props}>{children}</pre>
      <button type="button" className="copy-btn" onClick={onCopy}>Copy</button>
    </div>
  );
}

function TableEl(props) { return <table {...props} />; }
function ThEl(props) { return <th {...props} />; }
function TdEl(props) { return <td {...props} />; }

export default function MarkdownView({ markdown = '' }) {
  const processedMarkdown = preprocessMarkdown(markdown);
  return (
    <div className="mdx prose">
      <Suspense fallback={<p className="hint">Loading content…</p>}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkCallouts, remarkMath /*, remarkMermaid */]}
          rehypePlugins={[rehypeKatex]}
          components={{ a: AWithTarget, code: CodeBlock, pre: PreBlock, table: TableEl, th: ThEl, td: TdEl }}
        >
          {processedMarkdown}
        </ReactMarkdown>
      </Suspense>
    </div>
  );
}

function AWithTarget({ node, children, ...props }) {
  return <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>;
}